import type { SupportCenterAgendaEvent } from '../../types/supportCenterAppointments'
import type {
  SupportCenterUserAvailability,
  SupportCenterUserUnavailability,
} from '../../types/supportCenterSpecialties'

export type SlotOccupancy = { free: number; total: number }

export type SlotOccupancyMap = Map<string, SlotOccupancy>

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

export function occupancyKey(dateYmd: string, hour: number): string {
  return `${dateYmd.slice(0, 10)}-${hour}`
}

function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
}

function parseScheduled(value: string): Date | null {
  const d = new Date(value.includes('T') ? value : value.replace(' ', 'T'))
  return Number.isNaN(d.getTime()) ? null : d
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

function isYmdInRange(ymd: string, startsOn: string, endsOn: string): boolean {
  const day = ymd.slice(0, 10)
  return day >= startsOn.slice(0, 10) && day <= endsOn.slice(0, 10)
}

function isUnavailableAt(
  rows: SupportCenterUserUnavailability[],
  userId: string,
  dateYmd: string,
  hour: number
): boolean {
  return rows.some((row) => {
    if (row.user_id !== userId) return false
    if (!isYmdInRange(dateYmd, row.starts_on, row.ends_on)) return false
    if (row.all_day) return true
    return row.hours.includes(hour)
  })
}

function worksAt(
  availability: SupportCenterUserAvailability | undefined,
  weekday: number,
  hour: number
): boolean {
  if (!availability) return false
  if (availability.weekdays.length > 0 && !availability.weekdays.includes(weekday)) {
    return false
  }
  if (availability.hours.length === 0) return false
  return availability.hours.includes(hour)
}

/**
 * Ocupação a partir dos slots livres da IA-API (um profissional).
 * Célula livre se existir horário livre naquela hora.
 * `dayCount` default 5 (semana útil); use 1 para visão dia.
 */
export function buildOccupancyFromApiSlots(
  slots: Array<{ horario: string; scheduled_at: string }>,
  rangeStart: Date,
  hourStart: number,
  hourEnd: number,
  dayCount = 5
): SlotOccupancyMap {
  const freeHours = new Set<string>()
  for (const slot of slots) {
    const when = parseScheduled(slot.scheduled_at)
    if (!when) continue
    freeHours.add(occupancyKey(toDateKey(when), when.getHours()))
  }

  const map: SlotOccupancyMap = new Map()
  const safeStart = Math.min(hourStart, hourEnd)
  const safeEnd = Math.max(hourStart, hourEnd)
  const days = Math.max(1, dayCount)
  for (let d = 0; d < days; d++) {
    const day = addDays(rangeStart, d)
    const ymd = toDateKey(day)
    for (let hour = safeStart; hour <= safeEnd; hour++) {
      const key = occupancyKey(ymd, hour)
      map.set(key, { free: freeHours.has(key) ? 1 : 0, total: 1 })
    }
  }
  return map
}

type LocalOccupancyInput = {
  /** Início do intervalo (segunda na semana, ou o próprio dia na visão dia). */
  rangeStart: Date
  /** Quantidade de dias na grade (5 na semana útil, 1 no dia). */
  dayCount?: number
  hourStart: number
  hourEnd: number
  professionalIds: string[]
  availabilities: SupportCenterUserAvailability[]
  unavailabilities: SupportCenterUserUnavailability[]
  events: SupportCenterAgendaEvent[]
}

/**
 * Ocupação local (filtro "Todos"): grade + indisponibilidade + agendamentos abertos.
 * Não consulta Outlook — só grade cadastrada e eventos do intervalo.
 */
export function buildOccupancyLocal(input: LocalOccupancyInput): SlotOccupancyMap {
  const {
    rangeStart,
    dayCount = 5,
    hourStart,
    hourEnd,
    professionalIds,
    availabilities,
    unavailabilities,
    events,
  } = input

  const availByUser = new Map<string, SupportCenterUserAvailability>()
  for (const row of availabilities) {
    if (!availByUser.has(row.user_id)) availByUser.set(row.user_id, row)
  }

  const openBusy = new Set<string>()
  for (const event of events) {
    if (event.status !== 'marcado' && event.status !== 'reagendado') continue
    const userId = event.professional_user_id?.trim()
    if (!userId) continue
    const when = parseScheduled(event.scheduled_at)
    if (!when) continue
    openBusy.add(`${userId}|${occupancyKey(toDateKey(when), when.getHours())}`)
  }

  const map: SlotOccupancyMap = new Map()
  const safeStart = Math.min(hourStart, hourEnd)
  const safeEnd = Math.max(hourStart, hourEnd)
  const ids = professionalIds.filter(Boolean)
  const days = Math.max(1, dayCount)

  for (let d = 0; d < days; d++) {
    const day = addDays(rangeStart, d)
    const ymd = toDateKey(day)
    const weekday = day.getDay()
    for (let hour = safeStart; hour <= safeEnd; hour++) {
      let total = 0
      let free = 0
      for (const userId of ids) {
        if (!worksAt(availByUser.get(userId), weekday, hour)) continue
        if (isUnavailableAt(unavailabilities, userId, ymd, hour)) continue
        total += 1
        if (!openBusy.has(`${userId}|${occupancyKey(ymd, hour)}`)) {
          free += 1
        }
      }
      map.set(occupancyKey(ymd, hour), { free, total })
    }
  }
  return map
}
